import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";
import axios from "axios";
import API_ENDPOINTS from "config/api";

export default function PlanCuentas() {
  const [cuentas, setCuentas] = useState([]);
  const [filteredCuentas, setFilteredCuentas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTipo, setFilterTipo] = useState("todos");
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCuenta, setCurrentCuenta] = useState({
    codigo: "",
    nombre: "",
    tipo: "activo",
    subtipo: "",
    nivel: 1,
    cuenta_padre_id: null,
    descripcion: "",
    naturaleza: "deudora", // deudora o acreedora
    activa: true,
  });

  const tiposCuentas = [
    { value: "activo", label: "Activo", color: "info" },
    { value: "pasivo", label: "Pasivo", color: "warning" },
    { value: "capital", label: "Capital", color: "success" },
    { value: "ingreso", label: "Ingreso", color: "primary" },
    { value: "gasto", label: "Gasto", color: "error" },
    { value: "costo", label: "Costo", color: "secondary" },
  ];

  const [loading, setLoading] = useState(true);

  // Cargar datos desde la API
  const fetchCuentas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_ENDPOINTS.CONTABILIDAD.CUENTAS);
      setCuentas(response.data);
      setFilteredCuentas(response.data);
    } catch (error) {
      console.error("Error fetching cuentas:", error);
      // Fallback a datos estáticos si la API falla (opcional)
      const cuentasIniciales = [
        {
          id: 1,
          codigo: "1000",
          nombre: "ACTIVOS",
          tipo: "activo",
          subtipo: "principal",
          nivel: 1,
          naturaleza: "deudora",
          saldo: 1250000,
          activa: true,
        },
        // ... otros datos estáticos para desarrollo si es necesario
      ];
      setCuentas(cuentasIniciales);
      setFilteredCuentas(cuentasIniciales);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCuentas();
  }, []);

  // Filtrar cuentas
  useEffect(() => {
    let result = cuentas;

    if (searchTerm) {
      result = result.filter(
        (cuenta) =>
          cuenta.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cuenta.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterTipo !== "todos") {
      result = result.filter((cuenta) => cuenta.tipo === filterTipo);
    }

    setFilteredCuentas(result);
  }, [searchTerm, filterTipo, cuentas]);

  const handleOpenDialog = (cuenta = null) => {
    if (cuenta) {
      setCurrentCuenta(cuenta);
      setEditMode(true);
    } else {
      setCurrentCuenta({
        codigo: "",
        nombre: "",
        tipo: "activo",
        subtipo: "",
        nivel: 1,
        cuenta_padre_id: null,
        descripcion: "",
        naturaleza: "deudora",
        activa: true,
      });
      setEditMode(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCuenta({
      codigo: "",
      nombre: "",
      tipo: "activo",
      subtipo: "",
      nivel: 1,
      cuenta_padre_id: null,
      descripcion: "",
      naturaleza: "deudora",
      activa: true,
    });
  };

  const handleSaveCuenta = async () => {
    try {
      if (editMode) {
        await axios.put(API_ENDPOINTS.CONTABILIDAD.CUENTA(currentCuenta.id), currentCuenta);
      } else {
        await axios.post(API_ENDPOINTS.CONTABILIDAD.CUENTAS, currentCuenta);
      }
      fetchCuentas();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving cuenta:", error);
      alert("Error al guardar la cuenta. Por favor intente de nuevo.");
    }
  };

  const handleDeleteCuenta = async (id) => {
    if (window.confirm("¿Está seguro de eliminar esta cuenta?")) {
      try {
        await axios.delete(API_ENDPOINTS.CONTABILIDAD.CUENTA(id));
        fetchCuentas();
      } catch (error) {
        console.error("Error deleting cuenta:", error);
        alert("Error al eliminar la cuenta.");
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  const getTipoColor = (tipo) => {
    const tipoObj = tiposCuentas.find((t) => t.value === tipo);
    return tipoObj ? tipoObj.color : "default";
  };

  const columns = [
    { Header: "código", accessor: "codigo", width: "10%", align: "left" },
    { Header: "nombre de cuenta", accessor: "nombre", width: "30%", align: "left" },
    { Header: "tipo", accessor: "tipo", align: "center" },
    { Header: "nivel", accessor: "nivel", align: "center" },
    { Header: "naturaleza", accessor: "naturaleza", align: "center" },
    { Header: "saldo", accessor: "saldo", align: "right" },
    { Header: "acciones", accessor: "acciones", align: "center" },
  ];

  const rows = filteredCuentas.map((cuenta) => ({
    codigo: (
      <MDTypography variant="caption" fontWeight="medium">
        {cuenta.codigo}
      </MDTypography>
    ),
    nombre: (
      <MDBox ml={cuenta.nivel * 2}>
        <MDTypography variant="caption" fontWeight={cuenta.nivel === 1 ? "bold" : "medium"}>
          {cuenta.nombre}
        </MDTypography>
      </MDBox>
    ),
    tipo: <Chip label={cuenta.tipo} size="small" color={getTipoColor(cuenta.tipo)} />,
    nivel: (
      <MDTypography variant="caption" fontWeight="medium">
        {cuenta.nivel}
      </MDTypography>
    ),
    naturaleza: (
      <Chip
        label={cuenta.naturaleza}
        size="small"
        color={cuenta.naturaleza === "deudora" ? "info" : "warning"}
      />
    ),
    saldo: (
      <MDTypography variant="caption" fontWeight="medium">
        {formatCurrency(cuenta.saldo || 0)}
      </MDTypography>
    ),
    acciones: (
      <MDBox>
        <Tooltip title="Editar">
          <IconButton onClick={() => handleOpenDialog(cuenta)} size="small" color="info">
            <Icon>edit</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar">
          <IconButton
            onClick={() => handleDeleteCuenta(cuenta.id)}
            size="small"
            color="error"
            disabled={cuenta.nivel === 1}
          >
            <Icon>delete</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  }));

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              {/* Encabezado y filtros */}
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h6" fontWeight="medium">
                  Catálogo de Cuentas Contables
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Icon>add</Icon>}
                  onClick={() => handleOpenDialog()}
                >
                  Nueva Cuenta
                </MDButton>
              </MDBox>

              <Alert severity="info" sx={{ mb: 3 }}>
                El Plan de Cuentas es la estructura fundamental del sistema contable. Define todas
                las cuentas que se utilizarán para registrar las operaciones financieras.
              </Alert>

              {/* Filtros */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                  <MDInput
                    fullWidth
                    label="Buscar por código o nombre"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <Icon>search</Icon>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Filtrar por tipo"
                    value={filterTipo}
                    onChange={(e) => setFilterTipo(e.target.value)}
                  >
                    <MenuItem value="todos">Todos los tipos</MenuItem>
                    {tiposCuentas.map((tipo) => (
                      <MenuItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              {/* Estadísticas rápidas */}
              <Grid container spacing={2} mb={3}>
                {tiposCuentas.map((tipo) => {
                  const totalCuentas = cuentas.filter((c) => c.tipo === tipo.value).length;
                  return (
                    <Grid item xs={6} md={2} key={tipo.value}>
                      <MDBox
                        textAlign="center"
                        py={2}
                        borderRadius="lg"
                        sx={{ bgcolor: `${tipo.color}.lighter` }}
                      >
                        <MDTypography variant="h5" fontWeight="bold" color={tipo.color}>
                          {totalCuentas}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {tipo.label}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Tabla de cuentas */}
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog para agregar/editar cuenta */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? "Editar Cuenta" : "Nueva Cuenta"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Código"
                value={currentCuenta.codigo}
                onChange={(e) => setCurrentCuenta({ ...currentCuenta, codigo: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Nombre de la Cuenta"
                value={currentCuenta.nombre}
                onChange={(e) => setCurrentCuenta({ ...currentCuenta, nombre: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Tipo de Cuenta"
                value={currentCuenta.tipo}
                onChange={(e) => setCurrentCuenta({ ...currentCuenta, tipo: e.target.value })}
              >
                {tiposCuentas.map((tipo) => (
                  <MenuItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Naturaleza"
                value={currentCuenta.naturaleza}
                onChange={(e) => setCurrentCuenta({ ...currentCuenta, naturaleza: e.target.value })}
              >
                <MenuItem value="deudora">Deudora</MenuItem>
                <MenuItem value="acreedora">Acreedora</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Nivel"
                value={currentCuenta.nivel}
                onChange={(e) =>
                  setCurrentCuenta({ ...currentCuenta, nivel: parseInt(e.target.value) })
                }
              >
                <MenuItem value={1}>1 - Principal</MenuItem>
                <MenuItem value={2}>2 - Grupo</MenuItem>
                <MenuItem value={3}>3 - Detalle</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Subtipo"
                value={currentCuenta.subtipo}
                onChange={(e) => setCurrentCuenta({ ...currentCuenta, subtipo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                multiline
                rows={3}
                value={currentCuenta.descripcion}
                onChange={(e) =>
                  setCurrentCuenta({ ...currentCuenta, descripcion: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleSaveCuenta} variant="gradient" color="info">
            {editMode ? "Actualizar" : "Guardar"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
